import React, { createContext, useContext, useMemo, useState, useCallback } from 'react'
import { useGetLessonOverviewQuery, useGetClassQuery, useGetQuestionOverviewQuery, useUpdateQuestionResultMutation } from 'util/rtk-api'
import { StudentEntry, SECTIONS } from 'types'
import { buildQuestionSlots, QuestionSlot } from 'util/grades-grid'

export type CellState = 'correct' | 'incorrect' | 'not-submitted' | 'not-applicable'

export type GradeCell = {
	state: CellState
	questionId?: string
	questionText?: string
	correctAnswer?: string
	studentAnswer?: string
	manuallyGraded?: boolean
}

export type StudentRow = {
	studentId: string
	studentName: string
	tier: string
	cells: GradeCell[]
	gradedCount: number
	overallPct: number | null
}

type SlotTierResult = {
	questionId: string
	questionText?: string
	correctAnswer?: string
	results: {
		studentId: string
		studentName: string
		correct: boolean
		answer: string
		manuallyGraded?: boolean
	}[]
}

type GradesDataContextType = {
	lessonName: string
	classId: string
	lessonId: string
	isLoading: boolean
	isAssembling: boolean
	error?: unknown
	slots: QuestionSlot[]
	roster: StudentEntry[]
	studentRows: StudentRow[]
	gradeCell: (studentId: string, slotIndex: number, correct: boolean) => void
}

const GradesDataContext = createContext<GradesDataContextType | null>(null)

export const useGradesData = () => {
	const ctx = useContext(GradesDataContext)
	if (!ctx) throw new Error('useGradesData must be used within GradesDataProvider')
	return ctx
}

/**
 * Fetches one question's full per-student breakdown and hands it up to the
 * provider -- invisible, renders nothing. One of these per real (tier,
 * slot) question, mirroring the per-row provider pattern already used on
 * the question results page.
 */
const SlotTierFetcher = ({
	classId, lessonId, questionId, section, onData,
}: {
	classId: string
	lessonId: string
	questionId: string
	section: string
	onData: (questionId: string, data: SlotTierResult) => void
}) => {
	const { data } = useGetQuestionOverviewQuery({ classId, lessonId, questionId, section: section as SECTIONS })

	React.useEffect(() => {
		if (!data) return
		onData(questionId, {
			questionId: data.questionId,
			questionText: data.questionText,
			correctAnswer: data.correctAnswer,
			results: data.results.map(r => ({
				studentId: r.studentId,
				studentName: r.studentName,
				correct: r.correct,
				answer: r.answer,
				manuallyGraded: r.manuallyGraded,
			})),
		})
	}, [data, questionId, onData])

	return null
}

export const GradesDataProvider = ({
	children,
	classId,
	lessonId,
}: {
	children: React.ReactNode
	classId: string
	lessonId: string
}) => {
	const { data: overview, isLoading: isOverviewLoading, error: overviewError } = useGetLessonOverviewQuery({ classId, lessonId })
	const { data: klass, isLoading: isClassLoading, error: classError } = useGetClassQuery({ classId })
	const [updateQuestionResult] = useUpdateQuestionResultMutation()

	const slots = useMemo(() => buildQuestionSlots(overview?.questions || []), [overview])
	const roster = useMemo(() => klass?.students || [], [klass])

	const [resultsByQuestionId, setResultsByQuestionId] = useState<Record<string, SlotTierResult>>({})
	const onSlotData = useCallback((questionId: string, data: SlotTierResult) => {
		setResultsByQuestionId(prev => ({ ...prev, [questionId]: data }))
	}, [])

	const neededQuestions = useMemo(() => {
		const list: { questionId: string; section: string }[] = []
		for (const slot of slots) {
			for (const tier of Object.keys(slot.byTier)) {
				list.push({ questionId: slot.byTier[tier].questionId, section: slot.section })
			}
		}
		return list
	}, [slots])

	const isAssembling = neededQuestions.length > 0 && neededQuestions.some(q => !resultsByQuestionId[q.questionId])

	const studentRows: StudentRow[] = useMemo(() => {
		return roster
			.filter(s => s.difficulty && s.difficulty !== 'Not assigned')
			.map(student => {
				let graded = 0
				const scored: number[] = []
				const cells: GradeCell[] = slots.map(slot => {
					const tierQuestion = slot.byTier[student.difficulty]
					if (!tierQuestion) return { state: 'not-applicable' }

					const slotResult = resultsByQuestionId[tierQuestion.questionId]
					if (!slotResult) return { state: 'not-applicable' } // still loading -- resolves to a real state once fetched

					const entry = slotResult.results.find(r => r.studentId === student.studentId)
					if (!entry) return { state: 'not-submitted', questionId: tierQuestion.questionId, questionText: slotResult.questionText, correctAnswer: slotResult.correctAnswer }

					graded += 1
					scored.push(entry.correct ? 100 : 0)
					return {
						state: entry.correct ? 'correct' : 'incorrect',
						questionId: tierQuestion.questionId,
						questionText: slotResult.questionText,
						correctAnswer: slotResult.correctAnswer,
						studentAnswer: entry.answer,
						manuallyGraded: entry.manuallyGraded,
					}
				})

				return {
					studentId: student.studentId,
					studentName: student.studentName,
					tier: student.difficulty,
					cells,
					gradedCount: graded,
					overallPct: scored.length ? Math.round(scored.reduce((a, b) => a + b, 0) / scored.length) : null,
				}
			})
	}, [roster, slots, resultsByQuestionId])

	const gradeCell = useCallback((studentId: string, slotIndex: number, correct: boolean) => {
		const slot = slots[slotIndex]
		if (!slot) return
		const student = roster.find(s => s.studentId === studentId)
		if (!student) return
		const tierQuestion = slot.byTier[student.difficulty]
		if (!tierQuestion) return
		updateQuestionResult({
			studentId,
			lessonId,
			questionId: tierQuestion.questionId,
			score: correct ? 100 : 0,
			classId,
		})
	}, [slots, roster, updateQuestionResult, lessonId, classId])

	return (
		<GradesDataContext.Provider value={{
			lessonName: overview?.lessonName || '',
			classId,
			lessonId,
			isLoading: isOverviewLoading || isClassLoading,
			isAssembling,
			error: overviewError || classError,
			slots,
			roster,
			studentRows,
			gradeCell,
		}}>
			{neededQuestions.map(({ questionId, section }) => (
				<SlotTierFetcher
					key={questionId}
					classId={classId}
					lessonId={lessonId}
					questionId={questionId}
					section={section}
					onData={onSlotData}
				/>
			))}
			{children}
		</GradesDataContext.Provider>
	)
}
